// Pieces shared by the set pages: set types, links, the details fields, Member schemas, the runs table, and the version
// diff (blueprint-sets design "pages/SetPages.tsx").
import { type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useQueries, useQuery } from '@tanstack/react-query';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator, { type StatusIndicatorProps } from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import Textarea from '@cloudscape-design/components/textarea';
import type { z } from 'zod';
import {
  diffSetVersions,
  type BdaSchema,
  type Run,
  type SetMemberRef,
  type setSchema,
  type setVersionSchema,
  type VersionRef,
} from '@bda/shared';
import { api } from '../../api/client';
import { keys, useRuns } from '../../api/hooks';
import { type ResolvedSetMember } from './SetMembersEditor';
import { formatDate, formatDuration } from '../../lib/format';
import { errorMessage } from '../../lib/notify';
import { COVERAGE_FLOOR_LABEL, shareToPercent } from './coverageFloor';

export type BlueprintSet = z.infer<typeof setSchema>;
export type SetVersion = z.infer<typeof setVersionSchema>;
// ---------- shared pieces ----------

const refLabel = (ref: VersionRef | null | undefined): string =>
  ref === 'DRAFT' ? 'Draft set' : ref === null || ref === undefined ? '-' : `Version ${ref}`;
export const memberRefLabel = (ref: VersionRef): string => (ref === 'DRAFT' ? 'Current draft' : `Version ${ref}`);

export function RouterLink({ to, children, ariaLabel }: { to: string; children: ReactNode; ariaLabel?: string }) {
  const navigate = useNavigate();
  return (
    <Link
      href={to}
      ariaLabel={ariaLabel}
      onFollow={(e) => {
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </Link>
  );
}

export function DetailsFields({
  name,
  description,
  onName,
  onDescription,
  nameError,
  descriptionError,
}: {
  name: string;
  description: string;
  onName: (v: string) => void;
  onDescription: (v: string) => void;
  nameError?: string;
  descriptionError?: string;
}) {
  return (
    <Container header={<Header variant="h2">Details</Header>}>
      <SpaceBetween size="m">
        <FormField
          label="Name"
          description="Letters, digits, spaces, hyphens, or underscores (up to 128)."
          errorText={nameError}
          controlId="set-name"
        >
          <Input controlId="set-name" value={name} onChange={(e) => onName(e.detail.value)} />
        </FormField>
        <FormField
          label={
            <span>
              Description <i>- optional</i>
            </span>
          }
          errorText={descriptionError}
          controlId="set-description"
        >
          <Textarea
            controlId="set-description"
            value={description}
            rows={2}
            onChange={(e) => onDescription(e.detail.value)}
          />
        </FormField>
      </SpaceBetween>
    </Container>
  );
}

/**
 * The schema of each Member at its ref, for the Summary_Rules editor: from the set detail when the Member is unchanged,
 * else the blueprint's working schema (draft) or the version's schema. Shares the Members editor's query cache.
 * `undefined` while loading, `null` when it does not resolve.
 */
export function useMemberSchemas(members: SetMemberRef[], resolved: ResolvedSetMember[] | undefined) {
  const seedOf = (m: SetMemberRef) => resolved?.find((r) => r.blueprintId === m.blueprintId && r.ref === m.ref);
  const draftIds = [...new Set(members.filter((m) => m.ref === 'DRAFT' && !seedOf(m)).map((m) => m.blueprintId))];
  const versionRefs = [
    ...new Map(
      members.filter((m) => m.ref !== 'DRAFT' && !seedOf(m)).map((m) => [`${m.blueprintId}#${m.ref}`, m]),
    ).values(),
  ] as Array<{ blueprintId: string; ref: number }>;
  const drafts = useQueries({
    queries: draftIds.map((id) => ({ queryKey: keys.blueprint(id), queryFn: () => api.getBlueprint(id) })),
  });
  const versions = useQueries({
    queries: versionRefs.map((m) => ({
      queryKey: keys.version(m.blueprintId, m.ref),
      queryFn: () => api.getVersion(m.blueprintId, m.ref),
    })),
  });
  const schemaOf = (m: SetMemberRef): BdaSchema | null | undefined => {
    const seed = seedOf(m);
    if (seed) return seed.schema;
    const q =
      m.ref === 'DRAFT'
        ? drafts[draftIds.indexOf(m.blueprintId)]
        : versions[versionRefs.findIndex((v) => v.blueprintId === m.blueprintId && v.ref === m.ref)];
    if (!q) return undefined;
    if (q.isError) return null;
    if (!q.data) return undefined;
    return 'blueprint' in q.data ? q.data.blueprint.workingSchema : q.data.schema;
  };
  return { schemaOf, pending: members.some((m) => schemaOf(m) === undefined) };
}

const RUN_STATUS: Record<Run['status'], { type: StatusIndicatorProps.Type; label: string }> = {
  UPLOADING: { type: 'pending', label: 'Uploading' },
  QUEUED: { type: 'pending', label: 'Queued' },
  PROCESSING: { type: 'in-progress', label: 'Processing' },
  SUCCEEDED: { type: 'success', label: 'Succeeded' },
  FAILED: { type: 'error', label: 'Failed' },
};

export function SetRunsTable({ setId }: { setId: string }) {
  const runs = useRuns({ setId });
  const items = runs.data?.items ?? [];
  return (
    <Table<Run>
      header={
        <Header variant="h2" counter={runs.data ? `(${items.length}${runs.data.nextCursor ? '+' : ''})` : undefined}>
          Runs
        </Header>
      }
      loading={runs.isLoading}
      loadingText="Loading runs"
      items={items}
      trackBy="id"
      empty={
        <Box textAlign="center" color="inherit">
          {runs.isError ? errorMessage(runs.error) : 'No runs of this set yet.'}
        </Box>
      }
      columnDefinitions={[
        {
          id: 'file',
          header: 'Document',
          cell: (r) => <RouterLink to={`/runs/${r.id}`}>{r.fileName}</RouterLink>,
          isRowHeader: true,
        },
        { id: 'version', header: 'Set version', cell: (r) => refLabel(r.setVersion) },
        {
          id: 'status',
          header: 'Status',
          cell: (r) => <StatusIndicator type={RUN_STATUS[r.status].type}>{RUN_STATUS[r.status].label}</StatusIndicator>,
        },
        { id: 'started', header: 'Started', cell: (r) => formatDate(r.startedAt) },
        { id: 'duration', header: 'Duration', cell: (r) => formatDuration(r.durationMs) },
      ]}
    />
  );
}

/** Req 3.6: what changed from version `a` to version `b` (`diffSetVersions`), plus the Coverage_Floor. */
export function SetVersionDiff({ setId, a, b }: { setId: string; a: number; b: number }) {
  const qa = useQuery({ queryKey: keys.setVersion(setId, a), queryFn: () => api.getSetVersion(setId, a) });
  const qb = useQuery({ queryKey: keys.setVersion(setId, b), queryFn: () => api.getSetVersion(setId, b) });
  const header = <Header variant="h2">{`Compare version ${a} with version ${b}`}</Header>;
  if (qa.error || qb.error)
    return (
      <Container header={header}>
        <Alert type="error">{errorMessage(qa.error ?? qb.error)}</Alert>
      </Container>
    );
  if (!qa.data || !qb.data)
    return (
      <Container header={header}>
        <Spinner />
      </Container>
    );
  const va = qa.data.version;
  const vb = qb.data.version;
  const d = diffSetVersions(va, vb);
  const names = new Map([...va.memberSnapshot, ...vb.memberSnapshot].map((m) => [m.blueprintId, m.name]));
  const nameOf = (id: string) => names.get(id) ?? 'Unknown blueprint';
  const list = (label: string, lines: string[]) => (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      {lines.length === 0 ? (
        <Box color="text-status-inactive">None</Box>
      ) : (
        <ul aria-label={label}>
          {lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      )}
    </div>
  );
  return (
    <Container header={header}>
      <ColumnLayout columns={2} variant="text-grid">
        {list(
          'Members added',
          d.added.map((m) => `${nameOf(m.blueprintId)} (${memberRefLabel(m.ref)})`),
        )}
        {list(
          'Members removed',
          d.removed.map((m) => `${nameOf(m.blueprintId)} (${memberRefLabel(m.ref)})`),
        )}
        {list(
          'Version changed',
          d.changed.map((c) => `${nameOf(c.blueprintId)}: ${memberRefLabel(c.from)} → ${memberRefLabel(c.to)}`),
        )}
        <div>
          <Box variant="awsui-key-label">Packet summary rules</Box>
          <Box>{d.rulesChanged ? 'Changed' : 'Unchanged'}</Box>
          <Box variant="awsui-key-label" margin={{ top: 's' }}>
            {COVERAGE_FLOOR_LABEL}
          </Box>
          <Box>
            {va.coverageFloor === vb.coverageFloor
              ? `Unchanged (${shareToPercent(va.coverageFloor)} %)`
              : `${shareToPercent(va.coverageFloor)} % → ${shareToPercent(vb.coverageFloor)} %`}
          </Box>
        </div>
      </ColumnLayout>
    </Container>
  );
}
